"use client";

/**
 * Agenda Row
 * A single colored-accent row in the Agenda view: a clickable card wrapping `ScheduleItemDisplay`,
 * with a left accent bar (first label color, else primary), an optional "Now" marker for the
 * in-progress item, a registered ring, and the builder-only delete button.
 */

import type React from "react";

import type { ScheduleItemRow, ScheduleLabelRow } from "../../../../_logic/types/db-types";
import {
  getScheduleItemRegistrationState,
  type ViewerRegistrationKeys,
} from "../../../../_logic/utils/schedule-registration-match";
import { formatStartsIn } from "../../../../_logic/utils/schedule-utils";
import { ItemDeleteButton } from "./ItemDeleteButton";
import { getFirstLabelColor } from "./label-utils";
import { ScheduleItemDisplay } from "./ScheduleItemDisplay";

/** Render context shared by every row in an Agenda view (everything except the per-row item). */
export interface AgendaRowConfig {
  labels: ScheduleLabelRow[];
  labelMap: Map<number, ScheduleLabelRow>;
  primaryColor: string;
  onDelete?: (item: ScheduleItemRow) => void;
  onItemClick?: (item: ScheduleItemRow) => void;
  timeZone: string | null;
  isPublic?: boolean;
  viewerRegistrations: ViewerRegistrationKeys | null;
}

interface AgendaRowProps {
  /** Shared render context (labels, colors, handlers, timezone) — same for every row. */
  config: AgendaRowConfig;
  item: ScheduleItemRow;
  /** When true, shows a "Now" marker (the item is currently in progress). */
  isLive?: boolean;
  /** Minutes until the item starts. When set (and not live), shows a "Next · X min" badge. */
  startsInMinutes?: number;
}

/**
 * Renders one Agenda row.
 *
 * @param props - See AgendaRowProps.
 * @returns The row element.
 */
export function AgendaRow({ config, item, isLive = false, startsInMinutes }: AgendaRowProps): React.ReactElement {
  const {
    labels,
    labelMap,
    primaryColor,
    onDelete,
    onItemClick,
    timeZone,
    isPublic = false,
    viewerRegistrations,
  } = config;
  const accentColor = getFirstLabelColor(item, labelMap) ?? primaryColor;
  const registrationState = getScheduleItemRegistrationState(item, viewerRegistrations);
  const registrationRing =
    registrationState === "registered"
      ? "ring-1 ring-emerald-500/40"
      : registrationState === "waitlisted"
        ? "ring-1 ring-amber-500/40"
        : "";

  return (
    <div
      className={`group/item flex items-start justify-between gap-2 rounded-lg border border-l-4 border-gray-100 bg-white p-3 ${onItemClick ? "cursor-pointer touch-manipulation transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1" : ""} ${registrationRing}`}
      style={{ borderLeftColor: accentColor }}
      onClick={onItemClick ? () => onItemClick(item) : undefined}
      role={onItemClick ? "button" : undefined}
      tabIndex={onItemClick ? 0 : undefined}
      onKeyDown={
        onItemClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onItemClick(item);
              }
            }
          : undefined
      }
    >
      <ScheduleItemDisplay
        item={item}
        labels={labels}
        timeZone={timeZone}
        isPublic={isPublic}
        viewerRegistrations={viewerRegistrations}
      />
      <div className="flex shrink-0 items-center gap-1">
        {isLive ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: primaryColor }}>
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: primaryColor }} aria-hidden="true" />
            Now
          </span>
        ) : (
          startsInMinutes != null && (
            <span
              className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-1.5 py-0.5 text-xs font-semibold"
              style={{ color: primaryColor, backgroundColor: `${primaryColor}1a` }}
            >
              Next · {formatStartsIn(startsInMinutes)}
            </span>
          )
        )}
        {onDelete && <ItemDeleteButton item={item} onDelete={onDelete} />}
      </div>
    </div>
  );
}
